import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField
} from '@mui/material';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';
import { COICOP_DIVISIONS_GROUPS } from '../../../constants/coicop';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export default function CategoriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  
  // Create new category
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newGroupName, setNewGroupName] = useState(null); // Use null for Autocomplete
  const [newFixVariable, setNewFixVariable] = useState('Variable');
  const [newExclude, setNewExclude] = useState(false);
  
  // Get unique groups from COICOP hierarchy
  const availableGroups = useMemo(() => {
    const groups = new Set();
    COICOP_DIVISIONS_GROUPS.forEach(item => {
      groups.add(item.group);
    });
    return Array.from(groups).sort();
  }, []);
  
  // Delete dialog
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  
  // Pagination & sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortField, setSortField] = useState('category_name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/categories-with-groups`, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories (HTTP ${response.status})`);
      }

      const data = await response.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setError(e?.message || 'Unable to load categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Sort rows
  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // For fix_variable, convert to numbers for numeric sorting
      if (sortField === 'fix_variable') {
        const aNum = aVal === 'Variable' ? Infinity : Number(aVal) || 0;
        const bNum = bVal === 'Variable' ? Infinity : Number(bVal) || 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // For text fields, use string comparison
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, sortField, sortDirection]);

  // Paginate
  const paginatedRows = sortedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Create category
  const handleCreateCategory = useCallback(async (e) => {
    e.preventDefault();
    
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }

    if (!newGroupName) {
      setError('Group is required');
      return;
    }

    try {
      setStatus('');
      const response = await fetch(`${API_BASE}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_name: newCategoryName.trim(),
          group_name: newGroupName, // newGroupName is already a string from Autocomplete
          fix_variable: newFixVariable,
          exclude: newExclude ? 1 : 0
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err?.error || `Failed to create category (HTTP ${response.status})`);
      }

      setStatus(`✓ Created category "${newCategoryName}"`);
      setNewCategoryName('');
      setNewGroupName(null);
      setNewFixVariable('Variable');
      setNewExclude(false);
      
      // Refresh list
      fetchCategories();
    } catch (e) {
      setError(e?.message || 'Failed to create category');
    }
  }, [newCategoryName, newGroupName, newFixVariable, newExclude, fetchCategories]);

  // Delete category
  const handleDeleteCategory = useCallback(async () => {
    if (!categoryToDelete) return;

    try {
      setDeletingCategory(true);
      const response = await fetch(`${API_BASE}/api/categories/${encodeURIComponent(categoryToDelete.category_name)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to delete category (HTTP ${response.status})`);
      }

      setStatus(`✓ Deleted category "${categoryToDelete.category_name}"`);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (e) {
      setError(e?.message || 'Failed to delete category');
    } finally {
      setDeletingCategory(false);
    }
  }, [categoryToDelete, fetchCategories]);

  // Update fix_variable
  const handleUpdateFixVariable = useCallback(async (categoryName, value) => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_name: categoryName,
          fix_variable: value
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update category (HTTP ${response.status})`);
      }

      fetchCategories();
    } catch (e) {
      setError(e?.message || 'Failed to update category');
    }
  }, [fetchCategories]);

  // Update exclude
  const handleUpdateExclude = useCallback(async (categoryName, excluded) => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_name: categoryName,
          exclude: excluded ? 1 : 0
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update category (HTTP ${response.status})`);
      }

      fetchCategories();
    } catch (e) {
      setError(e?.message || 'Failed to update category');
    }
  }, [fetchCategories]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ASTITitle text="Categories Manager" />
      <ASTIText text="Create and manage categories. Set Fixed monthly values or Variable amounts. Mark as Excluded to hide from reports." />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {status && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus('')}>{status}</Alert>}

      {/* Create Category Form */}
      <Box sx={{
        backgroundColor: '#F0E3D0',
        p: 2,
        borderRadius: 1,
        mb: 3,
        border: '1px solid #D4A574'
      }}>
        <ASTIText text="Add New Category" />
        <Box component="form" onSubmit={handleCreateCategory} sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Category Name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Autocomplete
            options={availableGroups}
            value={newGroupName}
            onChange={(e, value) => setNewGroupName(value)}
            size="small"
            sx={{ flex: 1, minWidth: 300 }}
            renderInput={(params) => <TextField {...params} label="Group (required)" />}
          />
          <TextField
            label='Amount (Variable, or EUR with sign: +5000 for income, -40 for expenses)'
            value={newFixVariable}
            onChange={(e) => setNewFixVariable(e.target.value)}
            size="small"
            placeholder="Variable, or +5000, or -40"
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Checkbox
              checked={newExclude}
              onChange={(e) => setNewExclude(e.target.checked)}
              size="small"
            />
            <label>Exclude</label>
          </Box>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: '#C47A30',
              color: '#F0E3D0',
              '&:hover': { backgroundColor: '#A0572D' }
            }}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {/* Categories Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#E8D4C0' }}>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'category_name'}
                      direction={sortDirection}
                      onClick={() => handleSort('category_name')}
                    >
                      Category Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'group_name'}
                      direction={sortDirection}
                      onClick={() => handleSort('group_name')}
                    >
                      Group Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'fix_variable'}
                      direction={sortDirection}
                      onClick={() => handleSort('fix_variable')}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Excluded</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{row.category_name}</TableCell>
                    <TableCell>{row.group_name || '—'}</TableCell>
                    <TableCell align="center">
                      <TextField
                        value={row.fix_variable || 'Variable'}
                        onChange={(e) => handleUpdateFixVariable(row.category_name, e.target.value)}
                        size="small"
                        placeholder="Variable, +5000, or -40"
                        sx={{ width: '120px' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={Boolean(row.exclude)}
                        onChange={(e) => handleUpdateExclude(row.category_name, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => setCategoryToDelete(row)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(categoryToDelete)} onClose={() => !deletingCategory && setCategoryToDelete(null)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{categoryToDelete?.category_name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryToDelete(null)} disabled={deletingCategory}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCategory}
            color="error"
            variant="contained"
            disabled={deletingCategory}
          >
            {deletingCategory ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
